import { useCallback } from 'react';
import { Link, useNavigate } from 'react-router-dom';

import { Button, EditIcon } from '@occc/ui-design-system';
import { Page } from '@occc-app/design-system';
import { Can } from '@occc-app/App/PolicyBasedAccessControl';
import { UpdateRetailerNetwork } from '@occc-app/App/PolicyBasedAccessControl/userActions';
import { useConfig } from '@occc/config';

import { StoreDowntimeChart } from './components/store-downtime-chart';
import { BREADCRUMB_LINKS } from './retailer-network-overview-page.constants';
import { MyStores } from './components/my-stores/my-stores.component';
import { ActiveMemberPerformance } from './components/active-member-performance';
import { OfferPerformance } from './components/offer-performance';
import { useRetailerNetworkOverviewController } from './hooks/use-retailer-network-overview-controller';

import { useBreadcrumbLinks } from '../hooks/use-breadcrumb-links.hook';
import { useTransactionSetSelection } from '../hooks/use-transaction-set-selection.hook';
import { withRetailerNetwork, WithRetailerNetworkProps } from '../hocs/with-retailer-network.hoc';
import { RetailerNetworkDetailsSection } from '../retailer-network-details-page/components/retailer-network-details-section.component';
import { RETAILER_CONNECT_TITLE } from '../retailer-connect.constants';
import { routeCreator } from '../retailer-connect.utils';
import RetailerNetworkUsersConfigurationSection from '../retailer-network-users-configuration';
import { TransactionSetSelector } from '../components/transaction-set-selector';

export const RetailerNetworkOverviewPage = ({
  retailerNetwork,
  retailerNetworkUpdate,
}: WithRetailerNetworkProps) => {
  useBreadcrumbLinks(BREADCRUMB_LINKS);
  const { config } = useConfig();
  const isMobilePayMetricsEnabled = useConfig('retailerConnectMobilePayMetrics.enabled');
  const { transactionSet, setTransactionSet } = useTransactionSetSelection();

  const navigate = useNavigate();
  const {
    isRetailerNetworkSiteUserRole,
    isRetailerNetworkUserRole,
    locationDowntimeMetricsData,
    locationDowntimeMetricsDataError,
    isLocationDowntimeMetricsDataLoading,
    locationDowntimeMetricsOverview,
    locationDowntimeMetricsOverviewError,
    isLocationDowntimeMetricsOverviewLoading,
    transactionMetricsOverview,
    transactionMetricsOverviewError,
    isTransactionMetricsOverviewLoading,
    locationOfferMetricsOverview,
    locationOfferMetricsOverviewError,
    isLocationOfferMetricsOverviewLoading,
    locationOfferMetricsOverviewPaginationProps,
    // Sorting
    sortingParams,
    handleOfferPerformanceSort,
  } = useRetailerNetworkOverviewController(retailerNetwork.uuid, transactionSet);

  const handleNavigateToMyStores = useCallback(() => {
    navigate(routeCreator.myStores(retailerNetwork.uuid));
  }, [navigate, retailerNetwork.uuid]);
  const handleNavigateToPerformance = useCallback(() => {
    navigate(routeCreator.performance(retailerNetwork.uuid));
  }, [navigate, retailerNetwork.uuid]);

  return (
    <Page
      browserTabTitle={RETAILER_CONNECT_TITLE}
      pageHeaderProps={{
        title: RETAILER_CONNECT_TITLE,
        titleTestId: 'rc-overview-title',
        subtitle: retailerNetwork.name,
      }}
      containerProps={{ variant: 'md' }}
      actions={
        <>
          {isMobilePayMetricsEnabled && (
            <TransactionSetSelector
              value={transactionSet}
              onChange={setTransactionSet}
              variant='onDark'
            />
          )}
          <Can I={UpdateRetailerNetwork(retailerNetwork)}>
            <Button
              color='secondary'
              variant='contained'
              component={Link}
              startIcon={<EditIcon />}
              to={routeCreator.configurationUpdate(retailerNetwork.uuid)}
              role='button'
            >
              Edit Network
            </Button>
          </Can>
        </>
      }
    >
      {isRetailerNetworkSiteUserRole ? (
        <StoreDowntimeChart
          data={locationDowntimeMetricsData}
          hasError={!!locationDowntimeMetricsDataError}
          isLoading={isLocationDowntimeMetricsDataLoading}
        />
      ) : null}

      {isRetailerNetworkUserRole ? (
        <MyStores
          data={locationDowntimeMetricsOverview}
          hasError={!!locationDowntimeMetricsOverviewError}
          isLoading={isLocationDowntimeMetricsOverviewLoading}
          onViewMyStores={handleNavigateToMyStores}
        />
      ) : null}

      <RetailerNetworkDetailsSection retailerNetwork={retailerNetwork} />

      <ActiveMemberPerformance
        data={transactionMetricsOverview}
        hasError={!!transactionMetricsOverviewError}
        isLoading={isTransactionMetricsOverviewLoading}
        isViewPerformanceButtonVisible={!isRetailerNetworkSiteUserRole}
        onViewPerformance={handleNavigateToPerformance}
      />

      <OfferPerformance
        data={locationOfferMetricsOverview}
        hasError={!!locationOfferMetricsOverviewError}
        isLoading={isLocationOfferMetricsOverviewLoading}
        isRetailerNetworkSiteUserRole={isRetailerNetworkSiteUserRole}
        paginationProps={locationOfferMetricsOverviewPaginationProps}
        sortDirection={sortingParams?.direction ?? 'desc'}
        onViewPerformance={handleNavigateToPerformance}
        onSort={handleOfferPerformanceSort}
      />

      {config.retailerConnectRedesignV2.enabled && (
        <RetailerNetworkUsersConfigurationSection
          retailerNetwork={retailerNetwork}
          retailerNetworkUpdate={retailerNetworkUpdate}
          variant='section'
        />
      )}
    </Page>
  );
};

export default withRetailerNetwork(RetailerNetworkOverviewPage);
